/**
 * @fileoverview 종목별 DataDisplay에서 반복되던 renderTable 패턴 공통 헬퍼.
 * @description SoccerDataDisplay / BasketballDataDisplay / VolleyballDataDisplay /
 *              HockeyDataDisplay에 거의 동일하게 복사되어 있던 95줄짜리
 *              renderTable 함수를 단일 컴포넌트로 통합.
 */

import React from 'react';

interface SportTableStyles {
  tableContainer: React.CSSProperties;
  tableTitle: React.CSSProperties;
  tableWrapper: React.CSSProperties;
  table: React.CSSProperties;
  th: React.CSSProperties;
  td: React.CSSProperties;
}

interface SportTableProps {
  /** 백엔드에서 받은 테이블 데이터. headers/data 객체 또는 단순 배열 */
  tableData: any;
  /** 테이블 제목 (선택). 미지정 시 tableData.description 사용 */
  title?: string;
  /** 종목별 컴포넌트의 로컬 styles 객체 */
  styles: SportTableStyles;
}

/**
 * 백엔드 테이블 데이터를 정규화하여 헤더/행으로 분리.
 *
 * - tableData가 { headers, data } 형식이면 그대로 사용
 * - 배열만 있으면 첫 번째 비숫자 행을 헤더로 추론 (폴백)
 *
 * @returns { headers, rows, title } 또는 null (렌더 불가)
 */
function normalizeTableData(
  tableData: any,
  defaultTitle?: string
): { headers: string[]; rows: any[][]; title: string } | null {
  if (!tableData) return null;

  let dataArray: any[] = [];
  let description = '';
  let headers: string[] | null = null;

  if (tableData.data && Array.isArray(tableData.data)) {
    dataArray = tableData.data;
    description = tableData.description || '';
    if (Array.isArray(tableData.headers) && tableData.headers.length > 0) {
      headers = tableData.headers;
    }
  } else if (Array.isArray(tableData)) {
    dataArray = tableData;
  } else {
    return null;
  }

  if (dataArray.length === 0) return null;

  let processedRows: any[][] = [];
  let title = defaultTitle || description;

  if (headers) {
    // 백엔드 헤더가 명시적으로 제공된 경우 — 길이 일치 행만 사용
    processedRows = dataArray.filter(
      (row) => Array.isArray(row) && row.length === headers!.length
    );
  } else {
    // 폴백: 데이터에서 헤더 추론
    for (let i = 0; i < dataArray.length; i++) {
      const row = dataArray[i];
      if (!row || (Array.isArray(row) && row.length === 0)) continue;

      // 단일 요소 배열은 제목 후보
      if (Array.isArray(row) && row.length === 1) {
        if (i === 0 && !title) title = row[0];
        continue;
      }

      // 첫 번째 다중 컬럼 행을 헤더로 추론 (숫자가 거의 없는 행 우선)
      if (!headers && Array.isArray(row) && row.length > 1) {
        const firstCells = row.slice(0, Math.min(3, row.length)).join('');
        const hasNumbers = /\d/.test(firstCells);
        if (!hasNumbers || i === 0) {
          headers = row;
          continue;
        }
      }

      if (headers && Array.isArray(row) && row.length === headers.length) {
        processedRows.push(row);
      }
    }
  }

  if (!headers || processedRows.length === 0) return null;
  return { headers, rows: processedRows, title };
}

/**
 * 종목 공통 테이블 렌더 컴포넌트.
 *
 * styles props로 종목별 컴포넌트의 로컬 스타일을 주입받아 시각적 일관성을 유지.
 */
export const SportTable: React.FC<SportTableProps> = ({ tableData, title, styles }) => {
  const normalized = normalizeTableData(tableData, title);
  if (!normalized) return null;

  return (
    <div style={styles.tableContainer}>
      {normalized.title && <h4 style={styles.tableTitle}>{normalized.title}</h4>}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {normalized.headers.map((header, idx) => (
                <th key={idx} style={styles.th}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {normalized.rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} style={styles.td}>
                    {cell || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SportTable;
